import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { UserCircle, CheckCircle, ChatCircleDots, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function HumanInbox() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [h, tm] = await Promise.all([api.get("/human-inbox"), api.get("/team")]);
      setRows(h.data);
      setTeam(tm.data || []);
    } catch {
      toast.error("Error al cargar bandeja humano");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await api.post(`/contacts/${id}/resolve`);
      setRows(rows.filter((r) => r.contact.id !== id));
      toast.success("Marcado como resuelto");
    } catch { toast.error("Error"); }
  };

  const assign = async (contactId, memberId) => {
    try {
      const uid = memberId === "__none__" ? null : memberId;
      const { data } = await api.post(`/contacts/${contactId}/assign`, { user_id: uid });
      setRows(rows.map((r) => r.contact.id === contactId ? { ...r, assigned_to: data.assigned_to, assigned_to_name: data.assigned_to_name } : r));
      toast.success(uid ? `Asignado a ${data.assigned_to_name}` : "Sin asignar");
    } catch { toast.error("Error al asignar"); }
  };

  const urgencyBadge = (reason) => {
    if (!reason) return null;
    const lo = reason.toLowerCase();
    if (lo.includes("[ia:high]")) return { label: t("hi_urgency_high"), cls: "bg-[rgba(239,68,68,0.15)] text-[#EF4444]" };
    if (lo.includes("[ia:medium]")) return { label: t("hi_urgency_medium"), cls: "bg-[rgba(245,158,11,0.15)] text-[#F59E0B]" };
    if (lo.includes("[ia:low]")) return { label: t("hi_urgency_low"), cls: "bg-[rgba(59,130,246,0.15)] text-[#60A5FA]" };
    return null;
  };

  return (
    <div className="p-8 max-w-5xl mx-auto fade-in" data-testid="human-inbox">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2 flex items-center gap-2">
          <UserCircle size={12} weight="fill" /> Handoff
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">{t("hi_title")}</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">{t("hi_subtitle")}</p>
      </div>

      {loading && (
        <div className="text-sm text-[#71717A]">Cargando...</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-[#27272A] rounded-lg p-12 text-center" data-testid="human-inbox-empty">
          <CheckCircle size={32} weight="fill" className="mx-auto mb-3 text-[#10B981]" />
          <div className="text-sm text-[#A1A1AA]">{t("hi_empty")}</div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const badge = urgencyBadge(r.reason);
          const cleanReason = (r.reason || "").replace(/^\[IA:(high|medium|low)\]\s*/i, "");
          return (
          <div key={r.contact.id} className="border border-[#27272A] bg-[#121214] rounded-lg p-5 flex items-start gap-4" data-testid={`human-inbox-row-${r.contact.id}`}>
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EF4444] flex items-center justify-center text-white text-sm font-medium shrink-0">
              {r.contact.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-medium text-base tracking-tight truncate">{r.contact.name}</div>
                <span className="px-1.5 py-0.5 text-[9px] rounded bg-[rgba(245,158,11,0.15)] text-[#F59E0B] uppercase tracking-wider font-medium flex items-center gap-1">
                  <Warning size={10} weight="fill" /> handoff
                </span>
                {badge && (
                  <span className={`px-1.5 py-0.5 text-[9px] rounded uppercase tracking-wider font-medium ${badge.cls}`} data-testid={`urgency-${r.contact.id}`}>
                    {badge.label}
                  </span>
                )}
                <span className="text-[10px] text-[#71717A] font-mono-jb ml-auto shrink-0">
                  {t("hi_escalated_at")} {timeAgo(r.escalated_at)}
                </span>
              </div>
              <div className="text-xs text-[#71717A] font-mono">{r.contact.phone}</div>
              {cleanReason && (
                <div className="text-xs mt-2">
                  <span className="text-[#71717A]">{t("hi_reason")}: </span>
                  <span className="text-white">{cleanReason}</span>
                </div>
              )}
              {r.last_message && (
                <div className="mt-2 px-3 py-2 rounded-md bg-[#0A0A0C] border border-[#18181B] text-xs text-[#A1A1AA] italic truncate">
                  &ldquo;{r.last_message.text}&rdquo;
                </div>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Link to={`/app/inbox?contact=${r.contact.id}`}>
                  <Button size="sm" className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid={`open-chat-${r.contact.id}`}>
                    <ChatCircleDots size={14} className="mr-1.5" weight="fill" /> {t("hi_open")}
                  </Button>
                </Link>
                <Button size="sm" variant="outline" onClick={() => resolve(r.contact.id)} className="border-[#27272A] bg-[#18181B]" data-testid={`resolve-${r.contact.id}`}>
                  <CheckCircle size={14} className="mr-1.5" /> {t("hi_resolve")}
                </Button>
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="text-[10px] text-[#71717A] uppercase tracking-wider">{t("hi_assign")}</span>
                  <Select value={r.assigned_to || "__none__"} onValueChange={(v) => assign(r.contact.id, v)}>
                    <SelectTrigger className="h-8 w-44 bg-[#0A0A0C] border-[#27272A] text-xs" data-testid={`assign-${r.contact.id}`}>
                      <SelectValue placeholder={t("hi_unassigned")} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#27272A]">
                      <SelectItem value="__none__">{t("hi_unassigned")}</SelectItem>
                      {team.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.full_name} · {m.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
}
